/**
 * Фокус стандартных viewpoint ArchiMate 3.1 по **слоям метамодели** (modeling focus в духе спецификации).
 * Таблицы viewpoint в спеке задают конкретные типы элементов и связей; здесь — практичное приближение:
 * элемент «в фокусе», если его слой ({@link getLayerType}) входит в набор для выбранного viewpoint.
 * Вспомогательные элементы (заметка, junction, группа без слоя и т.п.) не затемняем.
 *
 * См. Open Group ArchiMate 3.1 — стандартный набор viewpoint; значения enum совпадают с XSD / {@link ARCHIMATE_VIEWPOINT_VALUES}.
 */
import * as CX from '../metamodel/Concept';
import {getLayerType, NOTE, JUNCTION, AND_JUNCTION, OR_JUNCTION} from '../util/ModelUtil';
import {STICKER} from '../util/CanvasNodeTypes';

/** Все слои ArchiMate — для viewpoint «Layered» и неизвестного enum (без затемнения). */
const ALL_LAYERS = new Set([
	CX.LAYER_MOTIVATION,
	CX.LAYER_STRATEGY,
	CX.LAYER_BUSINESS,
	CX.LAYER_APPLICATION,
	CX.LAYER_TECHNOLOGY,
	CX.LAYER_PHYSICAL,
	CX.LAYER_IMP_MIG,
	CX.LAYER_OTHER,
]);

/**
 * Множество слоёв, относящихся к modeling focus viewpoint.
 * @type {Readonly<Record<string, ReadonlySet<string>>>}
 */
export const VIEWPOINT_FOCUS_LAYERS = Object.freeze({
	Organization: new Set([CX.LAYER_BUSINESS, CX.LAYER_OTHER]),
	'Application Platform': new Set([CX.LAYER_APPLICATION, CX.LAYER_OTHER]),
	'Application Structure': new Set([CX.LAYER_APPLICATION, CX.LAYER_OTHER]),
	// Информационная структура: пассивная структура + смысл/материал (по спеке — Business/Application/Physical/Motivation)
	'Information Structure': new Set([
		CX.LAYER_BUSINESS,
		CX.LAYER_APPLICATION,
		CX.LAYER_PHYSICAL,
		CX.LAYER_MOTIVATION,
		CX.LAYER_OTHER,
	]),
	Technology: new Set([CX.LAYER_TECHNOLOGY, CX.LAYER_OTHER]),
	Layered: ALL_LAYERS,
	Physical: new Set([CX.LAYER_PHYSICAL, CX.LAYER_OTHER]),
	Product: new Set([CX.LAYER_BUSINESS, CX.LAYER_OTHER]),
	'Application Usage': new Set([CX.LAYER_BUSINESS, CX.LAYER_APPLICATION, CX.LAYER_OTHER]),
	'Technology Usage': new Set([CX.LAYER_APPLICATION, CX.LAYER_TECHNOLOGY, CX.LAYER_OTHER]),
	'Business Process Cooperation': new Set([CX.LAYER_BUSINESS, CX.LAYER_OTHER]),
	'Application Cooperation': new Set([CX.LAYER_APPLICATION, CX.LAYER_OTHER]),
	'Service Realization': new Set([CX.LAYER_BUSINESS, CX.LAYER_APPLICATION, CX.LAYER_OTHER]),
	'Implementation and Deployment': new Set([
		CX.LAYER_APPLICATION,
		CX.LAYER_TECHNOLOGY,
		CX.LAYER_PHYSICAL,
		CX.LAYER_OTHER,
	]),
	'Goal Realization': new Set([
		CX.LAYER_MOTIVATION,
		CX.LAYER_BUSINESS,
		CX.LAYER_APPLICATION,
		CX.LAYER_OTHER,
	]),
	'Goal Contribution': new Set([CX.LAYER_MOTIVATION, CX.LAYER_OTHER]),
	Principles: new Set([CX.LAYER_MOTIVATION, CX.LAYER_OTHER]),
	'Requirements Realization': new Set([
		CX.LAYER_MOTIVATION,
		CX.LAYER_BUSINESS,
		CX.LAYER_APPLICATION,
		CX.LAYER_OTHER,
	]),
	Motivation: new Set([CX.LAYER_MOTIVATION, CX.LAYER_OTHER]),
	Strategy: new Set([CX.LAYER_STRATEGY, CX.LAYER_OTHER]),
	'Capability Map': new Set([CX.LAYER_STRATEGY, CX.LAYER_BUSINESS, CX.LAYER_OTHER]),
	'Outcome Realization': new Set([CX.LAYER_MOTIVATION, CX.LAYER_STRATEGY, CX.LAYER_OTHER]),
	'Resource Map': new Set([CX.LAYER_STRATEGY, CX.LAYER_OTHER]),
	'Value Stream': new Set([
		CX.LAYER_STRATEGY,
		CX.LAYER_BUSINESS,
		CX.LAYER_APPLICATION,
		CX.LAYER_OTHER,
	]),
	Project: new Set([CX.LAYER_IMP_MIG, CX.LAYER_OTHER]),
	Migration: new Set([
		CX.LAYER_IMP_MIG,
		CX.LAYER_BUSINESS,
		CX.LAYER_APPLICATION,
		CX.LAYER_TECHNOLOGY,
		CX.LAYER_OTHER,
	]),
	'Implementation and Migration': new Set([
		CX.LAYER_IMP_MIG,
		CX.LAYER_BUSINESS,
		CX.LAYER_APPLICATION,
		CX.LAYER_TECHNOLOGY,
		CX.LAYER_OTHER,
	]),
	Stakeholder: new Set([CX.LAYER_MOTIVATION, CX.LAYER_OTHER]),
});

const AUXILIARY_ELEMENT_TYPES = new Set([
	NOTE,
	JUNCTION,
	AND_JUNCTION,
	OR_JUNCTION,
	CX.ARCHIMATE_GROUP,
	CX.OTHER_GROUPING,
	STICKER,
]);

/**
 * @param {string | null | undefined} viewpoint — значение ViewpointsEnum (как в модели)
 * @param {string | null | undefined} elementType — нормализованный тип элемента (как в node.data.elementType)
 * @returns {boolean} true = элемент в фокусе viewpoint (не затемнять)
 */
export function isElementTypeInViewpointFocus(viewpoint, elementType) {
	if (!viewpoint || String(viewpoint).trim() === '') return true;
	const vp = String(viewpoint).trim();
	const el = elementType != null ? String(elementType).trim() : '';
	if (!el) return true;
	if (AUXILIARY_ELEMENT_TYPES.has(el)) return true;

	const layers = VIEWPOINT_FOCUS_LAYERS[vp];
	if (!layers || layers === ALL_LAYERS) return true;

	const layer = getLayerType(el);
	if (layer == null || layer === '') return true;

	return layers.has(layer);
}

/**
 * Ребро «в фокусе», если оба конца — элементы в фокусе (по типам узлов).
 * @param {string | null | undefined} viewpoint
 * @param {boolean} sourceInFocus
 * @param {boolean} targetInFocus
 */
export function isEdgeEndpointsInViewpointFocus(viewpoint, sourceInFocus, targetInFocus) {
	if (!viewpoint || String(viewpoint).trim() === '') return true;
	return sourceInFocus && targetInFocus;
}
